import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import type { ContributionEntry } from '../types';

interface TodayContributionsListProps {
  contributions: ContributionEntry[];
  currentUserId: string;
  onEdit?: (contribution: ContributionEntry) => void;
  onApprove?: (contributionId: string) => Promise<void>;
  onReject?: (contributionId: string) => Promise<void>;
  onDelete?: (contributionId: string) => Promise<void>;
  isAdmin?: boolean;
}

const TodayContributionsList = ({ 
  contributions, 
  currentUserId, 
  onEdit, 
  onApprove, 
  onReject,
  onDelete,
  isAdmin = false 
}: TodayContributionsListProps) => {
  const totalAmount = contributions.reduce((sum, c) => sum + c.amount, 0);

  const getStatusColor = (status: ContributionEntry['status']) => {
    switch (status) {
      case 'verified':
        return 'text-success bg-success/10';
      case 'approved':
        return 'text-primary bg-primary/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status: ContributionEntry['status']) => {
    switch (status) {
      case 'verified':
        return 'CheckCircle';
      case 'approved':
        return 'Shield';
      case 'pending':
        return 'Clock';
      default:
        return 'Circle';
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (contributions.length === 0) {
    return (
      <div className="bg-card border border-border rounded-md p-8 text-center">
        <Icon name="DollarSign" size={48} className="text-muted-foreground mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Contributions Found</h3>
        <p className="text-sm text-muted-foreground">
          There are no contributions recorded for this group yet.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md overflow-hidden">
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Contribution History</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{contributions.length} entries</span>
            <div className="h-4 w-px bg-border" />
            <span className="text-sm font-semibold text-foreground">
              {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })} VT
            </span>
          </div>
        </div>
      </div>

      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {contributions.map((contribution) => (
          <div
            key={contribution.id}
            className="p-4 hover:bg-muted/30 transition-interactive"
          >
            <div className="flex items-start gap-3">
              <Image
                src={contribution.memberAvatar}
                alt={`${contribution.memberName} profile photo`}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {contribution.memberName}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Recorded by {contribution.recordedBy} • {formatTime(contribution.timestamp)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-semibold text-foreground">
                      {contribution.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })} VT
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(contribution.status)}`}>
                    <Icon name={getStatusIcon(contribution.status)} size={12} />
                    {contribution.status.charAt(0).toUpperCase() + contribution.status.slice(1)}
                  </span>

                  {contribution.notes && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Icon name="FileText" size={12} />
                      <span className="truncate max-w-[200px]">{contribution.notes}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 mt-3">
                  {contribution.status === 'pending' && isAdmin && (
                    <>
                      <button
                        onClick={() => onApprove?.(contribution.id)}
                        className="text-xs bg-success text-success-foreground px-3 py-1.5 rounded-sm hover:bg-success/90 transition-interactive font-medium flex items-center gap-1"
                      >
                        <Icon name="Check" size={12} />
                        Approve
                      </button>
                      <button
                        onClick={() => onReject?.(contribution.id)}
                        className="text-xs bg-destructive text-destructive-foreground px-3 py-1.5 rounded-sm hover:bg-destructive/90 transition-interactive font-medium flex items-center gap-1"
                      >
                        <Icon name="X" size={12} />
                        Reject
                      </button>
                    </>
                  )}

                  {contribution.canEdit && onEdit && (
                    <button
                      onClick={() => onEdit(contribution)}
                      className="text-xs text-primary hover:text-primary/80 transition-interactive font-medium flex items-center gap-1"
                    >
                      <Icon name="Edit" size={12} />
                      Edit Entry
                    </button>
                  )}

                  {contribution.canEdit && onDelete && (
                    <button
                      onClick={() => onDelete(contribution.id)}
                      className="text-xs text-destructive hover:text-destructive/80 transition-interactive font-medium flex items-center gap-1"
                    >
                      <Icon name="Trash2" size={12} />
                      Remove Contribution
                    </button>
                  )}
                </div>

                {!contribution.canEdit && contribution.recordedById !== currentUserId && contribution.status !== 'pending' && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Lock" size={12} />
                    <span>Cannot edit entries recorded by others</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TodayContributionsList;